import React, { useEffect, useState } from 'react';
import { FinanceApiError, FinanceService, IFinanceRecord, IPayoutResponse } from '../../services/finance.service';
import {
  Money,
  checkAgainstBalance,
  formatMoney,
  kopecksToDecimal,
  parseMoneyInput,
  toKopecks,
} from '../../utils/money';
import { PayoutResult, createPayoutSession } from '../../utils/payoutAttempts';
import './addFinanceRecordModal.css';
import './recordPayoutModal.css';

interface RecordPayoutModalProps {
  isOpen: boolean;
  record: IFinanceRecord | null;
  autoparkBalance: Money;
  onClose: () => void;
  onRecorded: (response: IPayoutResponse) => void;
  onAlreadyRecorded: () => void;
}

// edit — ввод суммы; pinned — исход прошлой отправки неизвестен, повторяем её же; already — выплата уже записана
type Mode = 'edit' | 'pinned' | 'already';

export const ALREADY_RECORDED_MESSAGE = 'Выплата уже записана, проверьте историю операций';
const UNKNOWN_OUTCOME_MESSAGE =
  'Не удалось получить ответ сервера. Нажмите «Повторить»: выплата не будет записана дважды.';

type PayoutSession = ReturnType<typeof createPayoutSession<IPayoutResponse>>;

// Сессии живут, пока открыта страница: если sessionStorage недоступен,
// попытка не теряется при закрытии и повторном открытии модалки.
const sessions = new Map<string, PayoutSession>();

function getSession(recordId: string): PayoutSession {
  let session = sessions.get(recordId);
  if (!session) {
    session = createPayoutSession<IPayoutResponse>(recordId, (attempt) =>
      FinanceService.recordPayout(recordId, {
        payoutUuid: attempt.payoutUuid,
        amount: kopecksToDecimal(attempt.draft.amountKopecks),
        note: attempt.draft.note.trim() || undefined,
      }),
    );
    sessions.set(recordId, session);
  }
  return session;
}

const REJECTION_MESSAGES: Record<string, string> = {
  payout_exceeds_remaining: 'Сумма больше остатка по записи',
  payout_exceeds_park_balance: 'Сумма больше баланса автопарка',
  invalid_amount: 'Некорректная сумма',
  record_not_found: 'Финансовая запись не найдена',
  record_without_autopark: 'Запись не привязана к автопарку',
};

function RecordPayoutModal({
  isOpen,
  record,
  autoparkBalance,
  onClose,
  onRecorded,
  onAlreadyRecorded,
}: RecordPayoutModalProps) {
  const [mode, setMode] = useState<Mode>('edit');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && record) {
      const attempt = getSession(record.id).pending();
      if (attempt) {
        setMode('pinned');
        setAmount(kopecksToDecimal(attempt.draft.amountKopecks));
        setNote(attempt.draft.note);
        setError(UNKNOWN_OUTCOME_MESSAGE);
      } else {
        setMode('edit');
        setAmount('');
        setNote('');
        setError('');
      }
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, record]);

  if (!isOpen || !record) return null;

  const handleClose = () => {
    if (!loading) onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Разрешаем только числа и одну точку/запятую
    if (/^\d*[.,]?\d*$/.test(value)) {
      setAmount(value);
      setError('');
    }
  };

  const validate = (): number | null => {
    const kopecks = parseMoneyInput(amount);

    if (!amount.trim()) {
      setError('Введите сумму');
      return null;
    }
    if (kopecks === null || kopecks <= 0) {
      setError('Введите корректную сумму');
      return null;
    }

    const remaining = toKopecks(record.remaining);
    if (Number.isNaN(remaining)) {
      setError('Не удалось определить остаток по записи');
      return null;
    }
    if (kopecks > remaining) {
      setError(REJECTION_MESSAGES.payout_exceeds_remaining);
      return null;
    }

    const balanceCheck = checkAgainstBalance(kopecks, autoparkBalance);
    if (balanceCheck === 'unknown') {
      setError('Не удалось определить баланс автопарка');
      return null;
    }
    if (balanceCheck === 'exceeds') {
      setError(REJECTION_MESSAGES.payout_exceeds_park_balance);
      return null;
    }

    return kopecks;
  };

  const handleResult = (result: PayoutResult<IPayoutResponse>) => {
    const apiError = result.error instanceof FinanceApiError ? result.error : undefined;

    switch (result.next) {
      case 'done':
        onRecorded(result.response!);
        onClose();
        break;
      case 'already':
        setMode('already');
        onAlreadyRecorded();
        break;
      case 'edit':
        setMode('edit');
        setError(REJECTION_MESSAGES[apiError?.code ?? ''] ?? apiError?.message ?? 'Не удалось записать выплату');
        break;
      case 'retry':
        setMode('pinned');
        setError(UNKNOWN_OUTCOME_MESSAGE);
        break;
    }
  };

  const run = async (request: Promise<PayoutResult<IPayoutResponse>>) => {
    setLoading(true);
    setError('');
    try {
      handleResult(await request);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (loading) return;
    const session = getSession(record.id);

    if (mode === 'pinned') {
      // Всегда повторяем ту же попытку: если выплата уже записана, сервер вернёт её же
      const retry = session.retry();
      if (retry) run(retry);
      return;
    }

    const kopecks = validate();
    if (kopecks === null) return;
    run(session.submit({ amountKopecks: kopecks, note }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };

  const pinned = mode === 'pinned';

  return (
    <div className="add-finance-modal-modal-overlay" onClick={handleBackdropClick}>
      <div className="record-payout-modal__layout">
        <button className="modal-close-btn" onClick={handleClose} disabled={loading} title="Закрыть">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="#3563E9" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <div className="add-finance-modal-wrapper">
          <div className="add-finance-modal record-payout-modal">
            <h2 className="modal-title">Записать выплату</h2>

            <div className="record-payout-modal__info">
              <span>Остаток по записи: {formatMoney(record.remaining)} Р</span>
              <span>Баланс автопарка: {formatMoney(autoparkBalance)} Р</span>
            </div>

            {mode === 'already' ? (
              <>
                <div className="record-payout-modal__notice">{ALREADY_RECORDED_MESSAGE}</div>
                <div className="modal-actions">
                  <button className="cancel-btn" onClick={onClose}>
                    Закрыть
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="input-container">
                  <input
                    type="text"
                    value={amount}
                    onChange={handleAmountChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Сумма выплаты"
                    className={`amount-input ${error && !pinned ? 'error' : ''}`}
                    readOnly={pinned || loading}
                    autoFocus={!pinned}
                  />
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Примечание (необязательно)"
                    className="record-payout-modal__note"
                    rows={2}
                    readOnly={pinned || loading}
                  />
                  {error && <div className={pinned ? 'record-payout-modal__notice' : 'error-message'}>{error}</div>}
                </div>

                <div className="modal-actions">
                  <button
                    className="confirm-btn record-payout-modal__confirm"
                    onClick={handleConfirm}
                    disabled={loading}
                  >
                    {loading ? 'Отправка...' : pinned ? 'Повторить' : 'Записать'}
                  </button>
                  <button className="cancel-btn" onClick={handleClose} disabled={loading}>
                    Отменить
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RecordPayoutModal;
